import React from 'react'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  CreateSpendingPolicyRuleGroupDocument,
  EditSpendingPolicyDetailsFragment,
  EditSpendingPolicyRuleGroupFragmentDoc,
  SpendingPoliciesRulesConfigurationDocument,
} from 'generated/resources/graphql'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EditRuleFormProps, EditRulesForm } from '../EditRuleForm'
import { CreateRuleGroupProps, OptionItem } from '../ruleFormHelpers'

interface Props {
  ruleGroup: CreateRuleGroupProps
  spendingPolicy: EditSpendingPolicyDetailsFragment
  onSuccess?: () => void
  onCancel: () => void
  timeFrameOptions: OptionItem[]
}

export const CreateRuleGroupForm: React.FC<Props> = ({
  ruleGroup,
  spendingPolicy,
  onSuccess,
  onCancel,
  timeFrameOptions,
}) => {
  const [createGroup] = useMutation(CreateSpendingPolicyRuleGroupDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceSpendingPolicySpendingPolicyRuleGroup,
    update(cache, { data }) {
      cache.modify({
        id: cache.identify(spendingPolicy),
        fields: {
          spendingPolicyRuleGroups(existingRuleGroupsRefs = []) {
            const newRuleGroupRef = cache.writeFragment({
              data: data?.financeMutations.createFinanceSpendingPolicySpendingPolicyRuleGroup
                .spendingPolicyRuleGroup,
              fragment: EditSpendingPolicyRuleGroupFragmentDoc,
              fragmentName: 'EditSpendingPolicyRuleGroup',
            })

            return [...existingRuleGroupsRefs, newRuleGroupRef]
          },
        },
      })
    },
    refetchQueries: [SpendingPoliciesRulesConfigurationDocument],
  })

  const mapRuleToFormValue = (identifier: string) => {
    return (
      ruleGroup.spendingPolicyRules
        .find((formRule) => formRule.identifier === identifier)
        ?.value.at(0) ?? ''
    )
  }
  const {
    isSubmitting,
    values: formValues,
    setErrors,
    errors,
    setFieldValue,
    handleSubmit,
    dirty,
  } = useForm<EditRuleFormProps>({
    enableReinitialize: false,
    validateOnChange: false,

    defaultValues: {
      parentRuleGroupId: ruleGroup.parentRuleGroup?.id.toString(),
      companyId: ruleGroup.companyId.toString(),
      description: ruleGroup.description,
      categoryId: ruleGroup.parentRuleGroupId
        ? mapRuleToFormValue('subcategory')
        : mapRuleToFormValue('category'),
      amount: mapRuleToFormValue('amount'),
      timeFrame: mapRuleToFormValue('time_frame'),
      currency: mapRuleToFormValue('currency'),
    },
    validate: (data) => {
      const validationErrors: FormErrors<EditRuleFormProps> = {}

      if (!data.timeFrame) {
        validationErrors.timeFrame = i18n.t('common.validations.empty')
      }
      if (!Number(data.amount)) {
        validationErrors.amount = i18n.t('common.validations.empty')
      }

      return validationErrors
    },
    onSubmit: async (submitValues) => {
      let handled = null

      const result = await createGroup({
        variables: {
          parentRuleGroupId: ruleGroup.parentRuleGroupId,
          spendingPolicyId: ruleGroup.spendingPolicyId.toString(),
          companyId: ruleGroup.companyId.toString(),
          description: submitValues.description,
          spendingPolicyRules: [
            {
              identifier: ruleGroup.parentRuleGroupId ? 'subcategory' : 'category',
              value: [submitValues.categoryId],
            },
            {
              identifier: 'amount',
              value: [submitValues.amount],
            },
            {
              identifier: 'time_frame',
              value: [submitValues.timeFrame],
            },
            {
              identifier: 'currency',
              value: [submitValues.currency],
            },
          ],
        },
      })
      handled = handleResult(result, (submitErrors) => {
        if (submitErrors) {
          setErrors(submitErrors)
          app.setFlash({
            type: 'wrong',
            message: i18n.t('spending_policies.details.form.update_error'),
          })
        }
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('spending_policies.details.form.update_success'),
        })

        onSuccess?.()
      }
      return handled
    },
  })

  const findRule = (identifier: string) => {
    return ruleGroup.spendingPolicyRules.find((rule) => rule.identifier === identifier)
  }
  const findParentRule = (identifier: string) => {
    return ruleGroup.parentRuleGroup?.spendingPolicyRules.find(
      (rule) => rule.identifier === identifier
    )
  }

  const updateFormValues = (identifier: keyof EditRuleFormProps, value: string) => {
    setFieldValue(identifier, value)
  }

  const selectedCategory = ruleGroup.parentRuleGroup
    ? findRule('subcategory')?.formatedValue
    : findRule('category')?.formatedValue

  const parentCategory = findParentRule('category')?.formatedValue

  return (
    <Box gap="s24" paddingBottom="s24">
      <Box flexDirection="row" alignItems="center" gap="s4">
        {parentCategory && (
          <>
            <Text.Normal size="300" color="textPrimary" weight="medium">
              {parentCategory}
            </Text.Normal>
            <Icon.Tiny color="grey800" icon="chevron-right" />
          </>
        )}
        <Text.Normal size="300" color="textPrimary" weight="medium">
          {selectedCategory}
        </Text.Normal>
      </Box>
      <EditRulesForm
        errors={errors}
        timeFrameOptions={timeFrameOptions}
        updateFormValues={updateFormValues}
        formValues={formValues}
      />

      <Box flexDirection="row" justifyContent="flexEnd" gap="s8">
        <Button type="secondary" onClick={() => onCancel()}>
          {i18n.t('common.cancel')}
        </Button>
        <Button loading={isSubmitting} onClick={() => handleSubmit()} disabled={!dirty}>
          {i18n.t('common.submit')}
        </Button>
      </Box>
    </Box>
  )
}
